import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {GetAnalysisFile} from 'analysis-tool/AnalysisTypes';

import ExternalFileChip from './ExternalFileChip';
import ExternalFileIFrame from './ExternalFileIFrame';
import ExternalFileImage from './ExternalFileImage';

interface ExternalFilePaneContentProps {
  file: GetAnalysisFile;
}

export function renderFilePreview(file: GetAnalysisFile): JSX.Element | null {
  switch (file.mimeType) {
    // In the future, we could support `HTML` as a first-class `EXTERNAL_FILE_TYPE`, so that embedded HTML
    // previews are supported across the whole platform (i.e., for all `ExternalFile` callers). For now, we
    // scope it down to Analyses.
    case 'text/html':
      return (
        <ExternalFileIFrame
          file={file}
          externalFileWarning="This block contains a file that is external to Benchling."
          // We allow scripts in the iframe because they are needed to render HTML reports generated by JMP.
          dangerouslyAllowScriptsInIFrame={true}
          // File ExternalFiles do not have their Content-Type metadata set, so they default to
          // application/octet-stream. Therefore, we need to tell the S3 signing endpoint to override the
          // Content-Type header to text/html.
          dangerouslyOverrideHTMLFileContentTypeHeader={true}
        />
      );
    case 'image/jpeg':
    case 'image/png':
      return <ExternalFileImage file={file} />;
    default:
      return null;
  }
}

/**
 * Render `Pane` content for the given `ExternalFile` for Analysis use cases
 *
 * As of writing, those use cases are:
 *   - Embedded HTML for HTML reports
 *   - File chip for JMP and JRP files
 *
 * This component is intentionally written to be `File`-agnostic, so that it may share code with other
 * `ExternalFile` callers more easily in the future.
 */
export default function ExternalFilePaneContent({file}: ExternalFilePaneContentProps): JSX.Element {
  const filePreview = renderFilePreview(file);
  return (
    filePreview ?? (
      <div className={css(styles.fileChip)}>
        <ExternalFileChip file={file} />
      </div>
    )
  );
}

const styles = StyleSheet.create({
  fileChip: {
    margin: '10px 12px',
  },
});
